"use client";

import { useCallback, useId, useState, type KeyboardEvent } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { FounderReplyBadge } from "@/components/shared/FounderReplyBadge";
import { FAQ_SECTION_COPY, splitQuestionPrefix } from "@/content/faq-section";
import type { FAQSectionData } from "@/sanity/types";
import { SOCIAL_LINKS, localizeAnchorHref } from "@/lib/constants";
import { cn } from "@/lib/utils";

/* ─── Social icon SVG paths ─────────────────────────────────── */

const SOCIAL_ICON_PATHS: Record<string, React.ReactNode> = {
  linkedin: (
    <path
      d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S.02 4.88.02 3.5C.02 2.12 1.13 1 2.5 1s2.48 1.12 2.48 2.5zM.34 8h4.32v12H.34V8zM8.65 8h4.13v1.64h.06c.58-1.09 1.99-2.24 4.1-2.24C21.31 7.4 22 10.26 22 14v6h-4.34v-5.32c0-1.27-.02-2.9-1.77-2.9-1.77 0-2.04 1.38-2.04 2.81V20H9.52V8h-.87z"
      fill="currentColor"
    />
  ),
  instagram: (
    <path
      d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.97.24 2.44.41.61.24 1.05.52 1.51.98.46.46.74.9.98 1.51.17.47.36 1.27.41 2.44.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.24 1.97-.41 2.44-.24.61-.52 1.05-.98 1.51-.46.46-.9.74-1.51.98-.47.17-1.27.36-2.44.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.97-.24-2.44-.41-.61-.24-1.05-.52-1.51-.98-.46-.46-.74-.9-.98-1.51-.17-.47-.36-1.27-.41-2.44C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.24-1.97.41-2.44.24-.61.52-1.05.98-1.51.46-.46.9-.74 1.51-.98.47-.17 1.27-.36 2.44-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.78.3-1.44.71-2.1 1.37S.94 3.36.63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.78.71 1.44 1.37 2.1s1.32 1.07 2.1 1.37c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.78-.3 1.44-.71 2.1-1.37s1.07-1.32 1.37-2.1c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.3-.78-.71-1.44-1.37-2.1S19.64.94 18.86.63C18.1.33 17.22.13 15.95.07 14.67.01 14.26 0 12 0zm0 5.84a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zM12 16a4 4 0 110-8 4 4 0 010 8zm6.41-11.85a1.44 1.44 0 100 2.88 1.44 1.44 0 000-2.88z"
      fill="currentColor"
    />
  ),
  facebook: (
    <path
      d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07c0 6.02 4.39 11.02 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.8-4.69 4.54-4.69 1.31 0 2.68.23 2.68.23v2.97h-1.51c-1.49 0-1.95.93-1.95 1.88v2.27h3.33l-.53 3.49h-2.8v8.44C19.61 23.09 24 18.09 24 12.07z"
      fill="currentColor"
    />
  ),
  x: (
    <path
      d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.63 7.58H.48l8.6-9.82L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.48 3.24H4.3l13.31 17.41z"
      fill="currentColor"
    />
  ),
};


/* ─── Accordion item ────────────────────────────────────────── */

function FAQItem({
  item,
  baseId,
  index,
  isOpen,
  onToggle,
}: {
  item: { question: string; answer: string };
  baseId: string;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const triggerId = `${baseId}-faq-trigger-${index}`;
  const panelId = `${baseId}-faq-panel-${index}`;
  const { prefix, text } = splitQuestionPrefix(item.question);

  return (
    <li
      className={cn(
        "rounded-[20px] border bg-white transition-[border-color,box-shadow] duration-300 motion-reduce:transition-none",
        "has-[button:focus-visible]:ring-2 has-[button:focus-visible]:ring-[#121212] has-[button:focus-visible]:ring-offset-2 has-[button:focus-visible]:ring-offset-foreground",
        isOpen
          ? "border-[#121212]/15 shadow-[0_1px_2px_rgba(18,18,18,0.04),0_8px_24px_-12px_rgba(18,18,18,0.12)]"
          : "border-[#121212]/[0.07] hover:border-[#121212]/20",
      )}
    >
      <h3>
        <button
          id={triggerId}
          type="button"
          data-faq-trigger=""
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className="group/faq flex w-full items-center justify-between gap-5 rounded-[20px] px-5 py-5 text-left focus-visible:outline-none md:gap-8 md:px-7 md:py-6"
        >
          <span className="text-[1.0625rem] font-normal leading-[1.35] tracking-[-0.015em] text-[#121212] md:text-[1.25rem] xl:text-[1.375rem]">
            {/* Sanity's "NN/" numbering stays in the HTML, out of sight. */}
            {prefix ? <span hidden>{prefix}</span> : null}
            {text}
          </span>
          <span
            aria-hidden="true"
            className={cn(
              "relative flex size-9 shrink-0 items-center justify-center rounded-full transition-colors duration-300 motion-reduce:transition-none md:size-10",
              isOpen
                ? "bg-accent text-[#121212]"
                : "bg-foreground text-[#121212] group-hover/faq:bg-[#121212] group-hover/faq:text-white",
            )}
          >
            <span className="absolute h-[1.5px] w-3.5 rounded-full bg-current" />
            <span
              className={cn(
                "absolute h-3.5 w-[1.5px] rounded-full bg-current transition-transform duration-300 motion-reduce:transition-none",
                isOpen && "scale-y-0",
              )}
            />
          </span>
        </button>
      </h3>

      {/*
        Collapsed answers stay in the server HTML (search engines and the
        FAQPage JSON-LD both rely on it). They are hidden with a 0fr grid row
        plus aria-hidden/inert, never unmounted.
      */}
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <p className="max-w-[68ch] px-5 pb-6 text-[1rem] font-light leading-[1.6] text-[#3d3d3d] md:px-7 md:pb-7 md:text-[1.0625rem]">
            {item.answer}
          </p>
        </div>
      </div>
    </li>
  );
}

/* ─── Next-step card ────────────────────────────────────────── */

function FAQContactCard({
  heading,
  text,
  pathname,
}: {
  heading: string;
  text: string;
  pathname: string;
}) {
  const { bookCall, contact, socialLabel } = FAQ_SECTION_COPY;

  return (
    <div className="rounded-[24px] bg-background p-6 text-white md:p-8">
      {heading ? (
        <h3 className="text-[1.5rem] font-light leading-[1.15] tracking-[-0.02em] md:text-[1.75rem]">
          {heading}
        </h3>
      ) : null}
      {text ? (
        <p className="mt-2 max-w-[380px] text-[0.9375rem] font-light leading-[1.5] text-muted md:text-[1rem]">
          {text}
        </p>
      ) : null}

      <FounderReplyBadge
        tone="dark"
        size="sm"
        className="mt-6 border-t border-white/10 pt-6"
      />

      <div className="mt-6 flex flex-col gap-3">
        {/* Plain anchor: CalendlyPopup intercepts /book into its dialog. */}
        <a
          href={bookCall.href}
          data-analytics-cta="faq_book_call"
          data-analytics-location="faq"
          className="group/cta inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full bg-accent px-6 text-[1rem] font-normal tracking-[-0.01em] text-[#121212] transition-colors hover:bg-[#5ee025] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {bookCall.label}
          <ArrowIcon className="size-4 transition-transform duration-300 group-hover/cta:translate-x-1 motion-reduce:transition-none" />
        </a>
        <Link
          // Routed like the header pill rather than hardcoded: this section
          // renders on pages without a contact form, where a bare "/#contact"
          // navigated to the homepage (finding conversion-n09tbe).
          href={localizeAnchorHref(contact.href, pathname)}
          data-analytics-cta="faq_contact"
          data-analytics-location="faq"
          className="inline-flex min-h-[52px] items-center justify-center rounded-full border border-white/25 px-6 text-[1rem] font-normal tracking-[-0.01em] text-white transition-colors hover:border-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {contact.label}
        </Link>
      </div>

      <div className="mt-6 flex items-center justify-between gap-4 border-t border-white/10 pt-5">
        <p className="text-[0.875rem] font-light text-muted">{socialLabel}</p>
        <ul className="flex items-center gap-2">
          {SOCIAL_LINKS.map((social) => (
            <li key={social.name}>
              <a
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
                className="flex size-10 items-center justify-center rounded-full text-muted ring-1 ring-white/15 transition-colors hover:text-white hover:ring-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  {SOCIAL_ICON_PATHS[social.icon]}
                </svg>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
      aria-hidden="true"
    >
      <path d="M2 8h11M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── FAQ section ───────────────────────────────────────────── */

/**
 * Shared FAQ section (homepage via PageBuilder, /about, /shopify-experts,
 * /shopify-services).
 *
 * Layout (Oct 2026 redesign):
 *   desktop  heading + a sticky dark "still have questions?" card (4/12)
 *            | a compact accordion of white cards (8/12)
 *   mobile   heading, accordion, then the card: the next step comes after
 *            the answers, not before them
 *
 * Accordion: several answers can be open at once, so a visitor can compare
 * them and opening one never moves the one they are reading. The first is
 * open on arrival to show that the rows open. Every question and answer is in
 * the server HTML whether open or not. Arrow keys, Home and End move between
 * questions (WAI-ARIA accordion pattern).
 */
interface FAQProps {
  data: FAQSectionData;
}

export function FAQ({ data }: FAQProps) {
  const pathname = usePathname();
  const baseId = useId();
  const items = data.items ?? [];
  const [openSet, setOpenSet] = useState<ReadonlySet<number>>(
    () => new Set(items.length > 0 ? [0] : []),
  );

  const toggle = useCallback((index: number) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const onListKeyDown = useCallback((event: KeyboardEvent<HTMLUListElement>) => {
    const target = event.target as HTMLElement;
    if (!target.matches("[data-faq-trigger]")) return;
    const triggers = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>("[data-faq-trigger]"),
    );
    const current = triggers.indexOf(target as HTMLButtonElement);
    let next = -1;
    if (event.key === "ArrowDown") next = (current + 1) % triggers.length;
    else if (event.key === "ArrowUp")
      next = (current - 1 + triggers.length) % triggers.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = triggers.length - 1;
    if (next < 0) return;
    event.preventDefault();
    triggers[next]?.focus();
  }, []);

  return (
    <section
      aria-labelledby="faq-heading"
      className="bg-foreground py-16 md:py-24 xl:py-28"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid gap-8 md:gap-10 lg:grid-cols-12 lg:grid-rows-[auto_1fr] lg:gap-x-8 lg:gap-y-10 xl:gap-x-12">
          {/* Heading */}
          <AnimatedSection className="lg:col-span-4 lg:row-start-1">
            <h2
              id="faq-heading"
              className="text-[2.5rem] font-light leading-none tracking-[-0.02em] text-[#121212] md:text-[3.5rem] xl:text-[4.5rem]"
            >
              {data.faqLabel}
            </h2>
            {data.faqDescription ? (
              <p className="mt-4 max-w-[340px] text-[1.0625rem] font-light leading-[1.45] text-[#3d3d3d] md:mt-5 md:text-[1.25rem]">
                {data.faqDescription}
              </p>
            ) : null}
          </AnimatedSection>

          {/* Accordion */}
          <ul
            onKeyDown={onListKeyDown}
            className="flex flex-col gap-2 md:gap-3 lg:col-span-8 lg:col-start-5 lg:row-span-2 lg:row-start-1"
          >
            {items.map((item, index) => (
              <FAQItem
                key={index}
                item={item}
                baseId={baseId}
                index={index}
                isOpen={openSet.has(index)}
                onToggle={() => toggle(index)}
              />
            ))}
          </ul>

          {/* Next step: sticky beside the list on desktop, after it on mobile */}
          <div className="lg:sticky lg:top-32 lg:col-span-4 lg:row-start-2 lg:self-start">
            <FAQContactCard
              heading={data.faqHeading}
              text={data.faqCtaText}
              pathname={pathname}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
